'use client';


import { useState } from 'react';
import Link from 'next/link';
import {
  Check,
  X,
  ArrowRight,
  ArrowLeft,
  Zap,
  Building2,
  Shield,
  BarChart3,
  Smartphone,
  Users,
  HelpCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';

/* ═══════════════════════════════════════
   Data — Market-Researched Pricing
   ═══════════════════════════════════════ */

const tiers = [
  {
    name: 'Starter',
    price: 199,
    annual: 1990,
    properties: 'Up to 25',
    users: '5 users',
    description: 'For solo operators and small facility teams getting organized.',
    highlighted: false,
    features: [
      'Unlimited work orders',
      'Basic asset tracking (100 assets)',
      'Photo/document attachments',
      'Mobile app (read-only)',
      'Email support (24h response)',
      '3-month data history',
      'Basic inspection forms',
      'Priority levels (low/med/high/urgent)',
    ],
    limitations: [
      'No preventive maintenance',
      'No QR code system',
      'No custom reports',
      'No API access',
      'No vendor portal',
    ],
    cta: 'Join Waitlist',
  },
  {
    name: 'Professional',
    price: 499,
    annual: 4990,
    properties: 'Up to 100',
    users: '25 users',
    description: 'For growing FM teams that need full visibility and coordination.',
    highlighted: true,
    badge: 'MOST POPULAR',
    features: [
      'Everything in Starter',
      'Full mobile app (read/write)',
      'Preventive maintenance scheduling',
      'Unlimited asset tracking',
      'QR code system (tag + scan)',
      'Customizable inspection templates',
      'Basic reporting & analytics',
      'API access (1,000 calls/day)',
      'Vendor portal invitations',
      'Chat support (business hours)',
      'Offline mode',
      'GPS location tracking',
    ],
    limitations: [
      'No advanced analytics/KPIs',
      'No SSO/SAML',
      'No white-label',
    ],
    cta: 'Join Waitlist',
  },
  {
    name: 'Enterprise',
    price: 1199,
    annual: 11990,
    properties: 'Unlimited',
    users: '100 users',
    description: 'For large operators needing advanced analytics and integrations.',
    highlighted: false,
    features: [
      'Everything in Professional',
      'Advanced reporting & custom dashboards',
      'KPI tracking (MTTR, MTBF, etc.)',
      'Automated report scheduling',
      'SSO/SAML authentication',
      'Full webhook support',
      'Stripe payment integration',
      'Vendor management portal (full)',
      'Priority support (4h response)',
      'Phone support included',
      '5-hour onboarding included',
      'API access (50,000 calls/day)',
      '2-year data history',
    ],
    limitations: [
      'No white-label branding',
      'No custom feature development',
    ],
    cta: 'Join Waitlist',
  },
  {
    name: 'Elite',
    price: 2499,
    annual: 24990,
    properties: 'Unlimited',
    users: 'Unlimited',
    description: 'For enterprise partners, resellers, and white-label deployments.',
    highlighted: false,
    features: [
      'Everything in Enterprise',
      'White-label branding (domain, logo, colors)',
      'Custom feature development (40 hrs/quarter)',
      'Dedicated account manager',
      'Quarterly strategic business reviews',
      'SLA guarantee (99.9% uptime)',
      'Custom integrations included',
      'Multi-language support',
      'Continuous data backups',
      'SOC 2 compliance path',
      'Unlimited API calls',
      'Unlimited data history',
      'Custom onboarding program',
    ],
    limitations: [],
    cta: 'Contact Sales',
  },
];

const addons = [
  {
    icon: BarChart3,
    name: 'Advanced Analytics',
    price: '$149-$299/mo',
    description: 'Predictive maintenance algorithms, anomaly detection, cost forecasting, and industry benchmarking.',
  },
  {
    icon: Zap,
    name: 'Custom Integrations',
    price: '$299/mo',
    description: 'Custom API development (10 hrs/mo), ERP/accounting sync, webhook config, and ongoing support.',
  },
  {
    icon: Shield,
    name: 'Compliance Reporting',
    price: '$199-$499/mo',
    description: 'Automated compliance docs, regulatory audit trails, certification tracking, and industry-specific packages.',
  },
  {
    icon: Smartphone,
    name: 'Mobile Premium',
    price: '$79-$199/mo',
    description: 'Offline work orders, GPS tracking, signature capture, voice notes, and technician performance tracking.',
  },
  {
    icon: Building2,
    name: 'Document Management',
    price: '$99-$199/mo',
    description: 'Unlimited storage, OCR text recognition, full-text search, version control, and expiration alerts.',
  },
  {
    icon: Users,
    name: 'Training Program',
    price: '$299-$799/mo',
    description: 'Monthly live training, custom facility training, certification tracking, and train-the-trainer programs.',
  },
];

const comparisonCategories = [
  {
    name: 'Core',
    features: [
      { name: 'Work orders', starter: 'Unlimited', professional: 'Unlimited', enterprise: 'Unlimited', elite: 'Unlimited' },
      { name: 'Properties', starter: '25', professional: '100', enterprise: 'Unlimited', elite: 'Unlimited' },
      { name: 'Users', starter: '5', professional: '25', enterprise: '100', elite: 'Unlimited' },
      { name: 'Asset tracking', starter: '100 assets', professional: 'Unlimited', enterprise: 'Unlimited', elite: 'Unlimited' },
      { name: 'Custom fields', starter: '3', professional: '10', enterprise: 'Unlimited', elite: 'Unlimited' },
    ],
  },
  {
    name: 'Maintenance',
    features: [
      { name: 'Preventive maintenance', starter: false, professional: true, enterprise: true, elite: true },
      { name: 'PM templates', starter: false, professional: '10', enterprise: 'Unlimited', elite: 'Unlimited' },
      { name: 'Condition-based PM', starter: false, professional: 'Limited', enterprise: true, elite: true },
      { name: 'QR code system', starter: false, professional: true, enterprise: true, elite: true },
    ],
  },
  {
    name: 'Reporting',
    features: [
      { name: 'Basic reports', starter: true, professional: true, enterprise: true, elite: true },
      { name: 'Custom report builder', starter: false, professional: 'Limited', enterprise: true, elite: true },
      { name: 'Advanced analytics/KPIs', starter: false, professional: false, enterprise: true, elite: true },
      { name: 'Automated scheduling', starter: false, professional: false, enterprise: true, elite: true },
      { name: 'Export PDF/Excel', starter: true, professional: true, enterprise: true, elite: true },
    ],
  },
  {
    name: 'Mobile & Access',
    features: [
      { name: 'Mobile app', starter: 'Read-only', professional: 'Full', enterprise: 'Full', elite: 'Full' },
      { name: 'Offline mode', starter: false, professional: true, enterprise: true, elite: true },
      { name: 'GPS tracking', starter: false, professional: true, enterprise: true, elite: true },
      { name: 'SSO/SAML', starter: false, professional: false, enterprise: true, elite: true },
    ],
  },
  {
    name: 'Integrations',
    features: [
      { name: 'REST API', starter: false, professional: '1K/day', enterprise: '50K/day', elite: 'Unlimited' },
      { name: 'Webhooks', starter: false, professional: false, enterprise: true, elite: true },
      { name: 'Stripe billing', starter: false, professional: false, enterprise: true, elite: true },
      { name: 'Vendor portal', starter: false, professional: true, enterprise: 'Full', elite: 'Full' },
      { name: 'White-label', starter: false, professional: false, enterprise: false, elite: true },
    ],
  },
  {
    name: 'Support',
    features: [
      { name: 'Email support', starter: '24h', professional: 'Business hrs', enterprise: '4h response', elite: 'Dedicated' },
      { name: 'Chat support', starter: false, professional: true, enterprise: true, elite: true },
      { name: 'Phone support', starter: false, professional: false, enterprise: true, elite: true },
      { name: 'Onboarding', starter: 'Self-serve', professional: '2 hours', enterprise: '5 hours', elite: 'Custom' },
      { name: 'Account manager', starter: false, professional: false, enterprise: false, elite: true },
      { name: 'Data retention', starter: '3 months', professional: 'All', enterprise: 'All', elite: 'All' },
    ],
  },
];

const faqs = [
  {
    q: 'When does Axis Datum launch?',
    a: 'We\'re targeting Q3 2026 for public launch. Join the waitlist to get early access and help shape the product.',
  },
  {
    q: 'Can I switch plans later?',
    a: 'Yes. Upgrade or downgrade anytime. When upgrading, you get immediate access to new features. When downgrading, changes take effect at your next billing cycle.',
  },
  {
    q: 'Do you offer annual discounts?',
    a: 'Yes — all plans offer a 5% discount when paid annually. That\'s effectively getting over half a month free.',
  },
  {
    q: 'What property types does Axis support?',
    a: 'All of them. Residential, commercial, retail, industrial, mixed-use, government, healthcare, education — Axis is built to be universal.',
  },
  {
    q: 'Is there a free trial?',
    a: 'We\'ll offer a 14-day free trial on all plans at launch. No credit card required to start.',
  },
  {
    q: 'What if I need more users than my plan allows?',
    a: 'Additional users are $49/user/month on any plan. Or upgrade to a higher tier for better per-user economics.',
  },
  {
    q: 'How does Axis compare to MaintainX, FMX, or Maximo?',
    a: 'Axis fills the gap between basic tools ($40/user) and enterprise behemoths ($3,000+/mo). Modern cloud-first design, transparent pricing, and rapid implementation — days, not months.',
  },
  {
    q: 'Do you offer consulting services?',
    a: 'Yes — through CoreOps, our consulting division. Operational audits, Lean Six Sigma process improvement, vendor strategy, and more. Consulting clients also get early access to Axis Datum.',
  },
];

/* ═══════════════════════════════════════
   Comparison Cell Renderer
   ═══════════════════════════════════════ */

function CellValue({ value }: { value: boolean | string }) {
  if (value === true) return <Check className="w-4 h-4 text-primary-500 mx-auto" />;
  if (value === false) return <X className="w-4 h-4 text-secondary-700 mx-auto" />;
  return <span className="text-xs text-secondary-300">{value}</span>;
}

/* ═══════════════════════════════════════
   Page
   ═══════════════════════════════════════ */

export default function PricingPage() {
  const [billing, setBilling] = useState<'monthly' | 'annual'>('monthly');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-white dark:bg-secondary-950">
      {/* Nav back */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-8">
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-secondary-500 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>
      </div>

      {/* Header */}
      <section className="pt-12 pb-8 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase bg-primary-500/10 text-primary-600 dark:text-primary-400 border border-primary-500/20 mb-6">
            Pricing
          </span>
          <h1 className="font-playfair text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Transparent Pricing.{' '}
            <span className="text-primary-600 dark:text-primary-400">No Surprises.</span>
          </h1>
          <p className="text-lg text-gray-500 dark:text-secondary-400 mb-8">
            Plans built for every stage — from solo operators to enterprise portfolios.
            All plans include a 14-day free trial at launch.
          </p>

          {/* Billing toggle */}
          <div className="inline-flex items-center gap-3 p-1 rounded-xl bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06]">
            <button
              type="button"
              onClick={() => setBilling('monthly')}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium transition-all',
                billing === 'monthly'
                  ? 'bg-white dark:bg-secondary-800 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200'
              )}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setBilling('annual')}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium transition-all',
                billing === 'annual'
                  ? 'bg-white dark:bg-secondary-800 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200'
              )}
            >
              Annual
              <span className="ml-1.5 text-xs text-primary-600 dark:text-primary-400 font-semibold">Save 5%</span>
            </button>
          </div>
        </div>
      </section>

      {/* Tier cards */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {tiers.map((tier) => (
              <div
                key={tier.name}
                className={cn(
                  'relative rounded-2xl p-6 flex flex-col',
                  tier.highlighted
                    ? 'bg-gray-50 dark:bg-gradient-to-br dark:from-[#1a1a2e] dark:to-[#141420] border-2 border-primary-500/40 shadow-lg dark:shadow-[0_0_30px_-10px_rgba(59,130,246,0.2)]'
                    : 'bg-white dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.06]'
                )}
              >
                {tier.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 bg-primary-500 text-white text-[10px] font-bold tracking-wider rounded-full shadow-lg">
                      {tier.badge}
                    </span>
                  </div>
                )}

                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">{tier.name}</h3>
                <p className="text-xs text-gray-500 dark:text-secondary-500 mb-4 min-h-[2rem]">{tier.description}</p>

                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-4xl font-bold text-gray-900 dark:text-white font-playfair">
                    ${billing === 'annual' ? Math.round(tier.annual / 12) : tier.price}
                  </span>
                  <span className="text-gray-400 dark:text-secondary-500 text-sm">/mo</span>
                </div>
                {billing === 'annual' && (
                  <p className="text-xs text-primary-600 dark:text-primary-400 mb-4">
                    ${tier.annual.toLocaleString()}/year (save ${(tier.price * 12 - tier.annual).toLocaleString()})
                  </p>
                )}
                {billing === 'monthly' && (
                  <p className="text-xs text-gray-400 dark:text-secondary-600 mb-4">
                    {tier.properties} properties &middot; {tier.users}
                  </p>
                )}

                <button
                  type="button"
                  className={cn(
                    'w-full py-2.5 rounded-lg text-sm font-semibold transition-all mb-6',
                    tier.highlighted
                      ? 'bg-primary-500 text-white hover:bg-primary-400 shadow-sm'
                      : 'bg-gray-100 dark:bg-white/[0.04] text-gray-700 dark:text-secondary-200 border border-gray-200 dark:border-white/[0.08] hover:bg-gray-200 dark:hover:bg-white/[0.08]'
                  )}
                >
                  {tier.cta}
                </button>

                <div className="space-y-2.5 flex-grow">
                  {tier.features.map((feature) => (
                    <div key={feature} className="flex items-start gap-2">
                      <Check className={cn(
                        'w-4 h-4 flex-shrink-0 mt-0.5',
                        tier.highlighted ? 'text-primary-500' : 'text-gray-400 dark:text-secondary-500'
                      )} />
                      <span className="text-sm text-gray-600 dark:text-secondary-300">{feature}</span>
                    </div>
                  ))}
                  {tier.limitations.map((limitation) => (
                    <div key={limitation} className="flex items-start gap-2 opacity-50">
                      <X className="w-4 h-4 flex-shrink-0 mt-0.5 text-gray-300 dark:text-secondary-700" />
                      <span className="text-sm text-gray-400 dark:text-secondary-600">{limitation}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-white/[0.06] to-transparent" />
      </div>

      {/* Feature comparison table */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-playfair text-3xl font-bold text-gray-900 dark:text-white mb-3">
              Full Feature Comparison
            </h2>
            <p className="text-gray-500 dark:text-secondary-400">Every feature, every tier, side by side.</p>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-white/[0.06]">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="bg-gray-50 dark:bg-white/[0.02]">
                  <th className="text-left px-5 py-4 text-sm font-semibold text-gray-900 dark:text-white w-[30%]">Feature</th>
                  <th className="text-center px-4 py-4 text-sm font-semibold text-gray-900 dark:text-white">Starter</th>
                  <th className="text-center px-4 py-4 text-sm font-semibold text-primary-600 dark:text-primary-400">Professional</th>
                  <th className="text-center px-4 py-4 text-sm font-semibold text-gray-900 dark:text-white">Enterprise</th>
                  <th className="text-center px-4 py-4 text-sm font-semibold text-gray-900 dark:text-white">Elite</th>
                </tr>
                <tr className="border-b border-gray-200 dark:border-white/[0.06]">
                  <td className="px-5 py-2" />
                  <td className="text-center px-4 py-2 text-xs text-gray-500 dark:text-secondary-500">$199/mo</td>
                  <td className="text-center px-4 py-2 text-xs text-primary-600 dark:text-primary-400 font-medium">$499/mo</td>
                  <td className="text-center px-4 py-2 text-xs text-gray-500 dark:text-secondary-500">$1,199/mo</td>
                  <td className="text-center px-4 py-2 text-xs text-gray-500 dark:text-secondary-500">$2,499/mo</td>
                </tr>
              </thead>
              <tbody>
                {comparisonCategories.map((category) => (
                  <>
                    <tr key={`cat-${category.name}`} className="bg-gray-50/50 dark:bg-white/[0.01]">
                      <td colSpan={5} className="px-5 py-3 text-xs font-bold text-gray-900 dark:text-secondary-200 uppercase tracking-wider">
                        {category.name}
                      </td>
                    </tr>
                    {category.features.map((feature) => (
                      <tr key={feature.name} className="border-t border-gray-100 dark:border-white/[0.03]">
                        <td className="px-5 py-3 text-sm text-gray-600 dark:text-secondary-400">{feature.name}</td>
                        <td className="text-center px-4 py-3"><CellValue value={feature.starter} /></td>
                        <td className="text-center px-4 py-3 bg-primary-500/[0.02]"><CellValue value={feature.professional} /></td>
                        <td className="text-center px-4 py-3"><CellValue value={feature.enterprise} /></td>
                        <td className="text-center px-4 py-3"><CellValue value={feature.elite} /></td>
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-white/[0.06] to-transparent" />
      </div>

      {/* Add-ons */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-playfair text-3xl font-bold text-gray-900 dark:text-white mb-3">
              Add-Ons &amp; Premium Services
            </h2>
            <p className="text-gray-500 dark:text-secondary-400">Available on any tier. Pay only for what you need.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {addons.map((addon) => (
              <div
                key={addon.name}
                className="p-6 rounded-2xl bg-white dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.06] hover:border-primary-300 dark:hover:border-primary-500/30 transition-colors"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center">
                    <addon.icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{addon.price}</span>
                </div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">{addon.name}</h3>
                <p className="text-sm text-gray-500 dark:text-secondary-400 leading-relaxed">{addon.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-white/[0.06] to-transparent" />
      </div>

      {/* Competitor comparison */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-playfair text-3xl font-bold text-gray-900 dark:text-white mb-3">
              How Axis Stacks Up
            </h2>
            <p className="text-gray-500 dark:text-secondary-400">Transparent pricing in a market that hides it.</p>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-white/[0.06]">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-white/[0.02]">
                  <th className="text-left px-5 py-4 text-sm font-semibold text-gray-900 dark:text-white">Platform</th>
                  <th className="text-center px-4 py-4 text-sm font-semibold text-gray-900 dark:text-white">Price Range</th>
                  <th className="text-center px-4 py-4 text-sm font-semibold text-gray-900 dark:text-white">Target</th>
                  <th className="text-center px-4 py-4 text-sm font-semibold text-gray-900 dark:text-white">Setup Time</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-gray-100 dark:border-white/[0.03] bg-primary-500/[0.03]">
                  <td className="px-5 py-3 text-sm font-semibold text-primary-600 dark:text-primary-400">Axis Datum</td>
                  <td className="text-center px-4 py-3 text-sm text-gray-600 dark:text-secondary-300">$199-$2,499/mo</td>
                  <td className="text-center px-4 py-3 text-sm text-gray-600 dark:text-secondary-300">SMB-Enterprise</td>
                  <td className="text-center px-4 py-3 text-sm text-gray-600 dark:text-secondary-300">Days</td>
                </tr>
                <tr className="border-t border-gray-100 dark:border-white/[0.03]">
                  <td className="px-5 py-3 text-sm text-gray-600 dark:text-secondary-400">MaintainX</td>
                  <td className="text-center px-4 py-3 text-sm text-gray-500 dark:text-secondary-500">$41/user/mo</td>
                  <td className="text-center px-4 py-3 text-sm text-gray-500 dark:text-secondary-500">Small teams</td>
                  <td className="text-center px-4 py-3 text-sm text-gray-500 dark:text-secondary-500">Days</td>
                </tr>
                <tr className="border-t border-gray-100 dark:border-white/[0.03]">
                  <td className="px-5 py-3 text-sm text-gray-600 dark:text-secondary-400">FMX</td>
                  <td className="text-center px-4 py-3 text-sm text-gray-500 dark:text-secondary-500">Custom quote</td>
                  <td className="text-center px-4 py-3 text-sm text-gray-500 dark:text-secondary-500">Education/Healthcare</td>
                  <td className="text-center px-4 py-3 text-sm text-gray-500 dark:text-secondary-500">Weeks</td>
                </tr>
                <tr className="border-t border-gray-100 dark:border-white/[0.03]">
                  <td className="px-5 py-3 text-sm text-gray-600 dark:text-secondary-400">Maintenance Connection</td>
                  <td className="text-center px-4 py-3 text-sm text-gray-500 dark:text-secondary-500">$110/user/mo</td>
                  <td className="text-center px-4 py-3 text-sm text-gray-500 dark:text-secondary-500">Mid-market</td>
                  <td className="text-center px-4 py-3 text-sm text-gray-500 dark:text-secondary-500">Weeks</td>
                </tr>
                <tr className="border-t border-gray-100 dark:border-white/[0.03]">
                  <td className="px-5 py-3 text-sm text-gray-600 dark:text-secondary-400">IBM Maximo</td>
                  <td className="text-center px-4 py-3 text-sm text-gray-500 dark:text-secondary-500">$3,150+/mo</td>
                  <td className="text-center px-4 py-3 text-sm text-gray-500 dark:text-secondary-500">Enterprise</td>
                  <td className="text-center px-4 py-3 text-sm text-gray-500 dark:text-secondary-500">Months</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-white/[0.06] to-transparent" />
      </div>

      {/* FAQ */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-playfair text-3xl font-bold text-gray-900 dark:text-white mb-3">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="rounded-xl border border-gray-200 dark:border-white/[0.06] overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors"
                >
                  <span className="text-sm font-medium text-gray-900 dark:text-white pr-4">{faq.q}</span>
                  <HelpCircle className={cn(
                    'w-4 h-4 flex-shrink-0 text-gray-400 dark:text-secondary-500 transition-transform',
                    openFaq === i && 'rotate-180'
                  )} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4">
                    <p className="text-sm text-gray-500 dark:text-secondary-400 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <div className="p-8 sm:p-12 rounded-2xl bg-gradient-to-br from-primary-500/10 to-primary-600/5 border border-primary-500/20">
            <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Ready to Streamline Your Operations?
            </h2>
            <p className="text-gray-500 dark:text-secondary-400 mb-8">
              Join the waitlist for early access. Launching Q3 2026.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                href="/#contact"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary-500 text-white font-semibold hover:bg-primary-400 transition-colors shadow-sm"
              >
                Join the Waitlist
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="https://coreops-landing.vercel.app"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] text-gray-700 dark:text-secondary-200 font-medium hover:bg-gray-50 dark:hover:bg-white/[0.06] transition-colors"
              >
                Need Consulting First?
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
